import { Toast } from '@base-ui/react/toast';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useI18n } from '../../i18n/i18n';
import { cn } from '../../lib/cn';

type Tone = 'info' | 'success' | 'danger';

/** Провайдер уведомлений: один на приложение, `Toaster` рисует их стопкой справа внизу. */
export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <Toast.Provider timeout={5000} limit={3}>
      {children}
      <Toaster />
    </Toast.Provider>
  );
}

/**
 * Короткое уведомление о результате действия: «откат пропустил поле, которое изменил другой»
 * (`REVERT_SKIPPED`), ошибка сохранения правки на холсте. Ошибки форм — `Notice`, а не тосты.
 */
export function useToast() {
  const manager = Toast.useToastManager();
  return {
    show: ({
      title,
      description,
      tone = 'info',
    }: {
      title: ReactNode;
      description?: ReactNode;
      tone?: Tone;
    }) => manager.add({ title, description, type: tone }),
  };
}

const TONES: Record<string, string> = {
  info: 'border-border',
  success: 'border-success',
  danger: 'border-danger',
};

function Toaster() {
  const { toasts } = Toast.useToastManager();
  const { t } = useI18n();
  return (
    <Toast.Portal>
      <Toast.Viewport className="fixed right-4 bottom-4 z-[60] flex w-80 flex-col gap-2 outline-none">
        {toasts.map((toast) => (
          <Toast.Root
            key={toast.id}
            toast={toast}
            className={cn(
              'flex items-start gap-3 rounded-lg border bg-card p-3 text-sm text-foreground shadow-popover transition-[opacity,transform] data-[ending-style]:translate-y-2 data-[ending-style]:opacity-0 data-[starting-style]:translate-y-2 data-[starting-style]:opacity-0',
              TONES[toast.type ?? 'info'],
            )}
          >
            <div className="min-w-0 flex-1 space-y-0.5">
              <Toast.Title className="font-medium" />
              <Toast.Description className="text-muted-foreground" />
            </div>
            <Toast.Close
              aria-label={t('common.close')}
              className="-m-1 inline-flex size-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-tint hover:text-foreground"
            >
              <X className="size-3.5" />
            </Toast.Close>
          </Toast.Root>
        ))}
      </Toast.Viewport>
    </Toast.Portal>
  );
}
